import uaAnalytics from '@utils/uaAnalytics';
import { sendEvents } from '@events/sendAnalytics';
import { localStorageAvailable } from '@utils/localStorage';

class Player {
  // eslint-disable-next-line no-useless-constructor
  constructor() {
    // do nothing
  }

  setup(classDetails, user) {
    this.source = '';
    this.totalWatchTime = 0;
    this.playedDuration = 0;
    this.classStartTime = 0;
    this.dvr = false;
    this.replay = false;
    this.isLive = 0;
    this.watchEventSent = false;
    this.svEventSent = false;
    this.svFifteenMinsEventSent = false;
    this.watchEvent = {};
    this.prevUACallTime = 0;
    this.sendCurrentWatchApi = false;
    this.classDetails = classDetails;
    this.user = user;
  }

  setupFlags = () => {
    this.watchEventSent = this.isWatchEventSent();
    this.svEventSent = this.shouldSendSVEvent();
    this.svFifteenMinsEventSent = this.shouldSendSVEventFifteenMins();
  };

  isWatchEventSent = () => {
    if (localStorageAvailable()) {
      const isLiveSessionsWatchEventSent = localStorage.getItem(
        `liveSessionsWatchEventSent[${this.classDetails?.uid}]`
      );
      if ([undefined, 'undefined'].includes(isLiveSessionsWatchEventSent)) {
        return false;
      }
      return JSON.parse(isLiveSessionsWatchEventSent);
    }
    return this.totalWatchTime > 600;
  };

  shouldSendWatchEvent = () => {
    return this.totalWatchTime > 600;
  };

  shouldSendSVEvent = () => {
    return this.totalWatchTime >= 300;
  };

  shouldSendSVEventFifteenMins = () => {
    return this.totalWatchTime >= 900;
  };

  getCompleteWatchTime = (isLiveDvr, isReplay) => {
    let watchTime = 0;
    if (!isReplay) {
      watchTime = this.classStartTime
        ? new Date().getTime() / 1000 - this.classStartTime / 1000
        : 0;
      if (isLiveDvr) {
        watchTime = this.playedDuration ? this.playedDuration : watchTime;
      }
    } else {
      watchTime = this.playedDuration - this.classStartTime;
    }
    return parseInt(watchTime.toFixed(2), 10);
  };

  sendUaWatch = (watchEventInterval) => {
    this.createWatchEvent();
    this.prevUACallTime = uaAnalytics.watchEventCallback(
      this.totalWatchTime,
      this.watchEvent,
      this.prevUACallTime,
      false,
      watchEventInterval
    );
    if (this.totalWatchTime === 1) {
      uaAnalytics.oneSecWatched(this.watchEvent);
      this.sendLessonPlay();
    }
  };

  sendAnalyticsEvent = (name, data) => sendEvents(name, data);

  getEventData = () => ({
    'Lesson ID': this.classDetails.uid,
    'Is Plus': true,
    'Goal Id':
      this.classDetails.programme?.goal?.uid ||
      this.classDetails.programme?.goal?.topology?.uid,
    'Goal Name':
      this.classDetails.programme?.goal?.name ||
      this.classDetails.programme?.goal?.topology?.name,
    'Is Special': this.classDetails.programme?.isSpecial,
    'Learner Username': this.user?.username,
    'Learner Id': this.user?.uid,
    user_id: this.user?.uid,
    'User Id': this.user?.uid,
    Time: new Date().getTime(),
    'Course Id': this.classDetails.programme?.uid,
    'Course Name': this.classDetails.programme?.name,
    'Educator Username': this.classDetails.author?.username,
    'Educator Id': this.classDetails.author?.uid
  });

  sendLessonPlay = () => {
    this.sendAnalyticsEvent('Lesson Play', {
      'Lesson Duration': this.classDetails.lessonDuration,
      Source: 'Direct',
      'Lesson Start Mode': 'UserPlay',
      ...this.getEventData()
    });
  };

  sendSV = () => {
    if (this.shouldSendSVEvent() && !this.svEventSent) {
      this.svEventSent = true;
      this.sendAnalyticsEvent('Successful View', this.getEventData());
    }
  };

  sendSubscriptionCardVisibleEventOnIframe = () => {
    this.sendAnalyticsEvent('Special Class: Live subscription nudge', {
      'Lesson Duration': this.classDetails.lessonDuration,
      Source: 'Direct',
      'Lesson Start Mode': 'UserPlay',
      'Last Primary Source': 'Special Class Live',
      ...this.getEventData()
    });
  };

  sendSubscriptionClickedOnIframe = () => {
    this.sendAnalyticsEvent('Plus: Get Subscription', {
      'Lesson Duration': this.classDetails.lessonDuration,
      Source: 'Direct',
      'Lesson Start Mode': 'UserPlay',
      'Last Primary Source': 'Special Class Live',
      ...this.getEventData()
    });
  };

  sendSVFifteenMins = () => {
    if (this.shouldSendSVEventFifteenMins() && !this.svFifteenMinsEventSent) {
      this.svFifteenMinsEventSent = true;
      this.sendAnalyticsEvent(
        'Successful View Fifteen Minutes',
        this.getEventData()
      );
    }
  };

  sendCurrentWatch = (handleOnSendWatched) => {
    if (
      this.getCompleteWatchTime(this.dvr, this.replay) >= 10 &&
      !this.sendCurrentWatchApi
    ) {
      this.sendCurrentWatchApi = true;
      if (handleOnSendWatched) handleOnSendWatched();
    }
  };

  sendLessonWatched = (handleOnSendWatchedPrevious) => {
    const isWatchEventSent = this.isWatchEventSent();
    const shouldSendWatchEvent = this.shouldSendWatchEvent();
    if (shouldSendWatchEvent && !isWatchEventSent) {
      if (handleOnSendWatchedPrevious) handleOnSendWatchedPrevious();
    }
  };

  createWatchEvent() {
    this.watchEvent.TOTAL_RUN_TIME = this.totalWatchTime;
    this.watchEvent.CURRENT_TIME = this.getCompleteWatchTime(
      this.dvr,
      this.replay
    );
    this.watchEvent.IS_LIVE = this.isLive;
    if (this.user) this.watchEvent.user_id = this.user.uid;
    if (this.classDetails.programme) {
      this.watchEvent.collection_slug = this.classDetails.programme.slug;
      if (this.classDetails.author) {
        this.watchEvent.user_uid = this.classDetails.author.uid;
      }
    }
    this.watchEvent.goal_id =
      this.classDetails?.goalUid ||
      this.classDetails?.goal?.uid ||
      this.classDetails?.programme?.goalUid ||
      this.classDetails?.programme?.goal?.uid ||
      null;
    this.watchEvent.video_player_type =
      this.classDetails.classType === 1 ? 4 : 3;
    this.watchEvent.uid = this.classDetails.uid;
    this.watchEvent.player_type = 'normal';
    this.watchEvent.source = this.source;
    this.watchEvent.total_video_length = this.classDetails?.videoDuration;
  }

  unmount = () => {
    if (this.watchEvent && Object.keys(this.watchEvent).length) {
      // Create watch event to send on an unmount player event
      this.createWatchEvent();
      if (
        this.watchEvent?.TOTAL_RUN_TIME &&
        this.watchEvent?.uid &&
        this.watchEvent?.user_uid
      )
        uaAnalytics.unmountChanges(this.watchEvent);
    }
    this.watchEvent = {};
  };
}

export default Player;
